<div class="activity-container">
  <div class="activity-header">
    <h1 class="activity-title">{{ 'ACTIVITY.TITLE' | translate }}</h1>
  </div>

  <mat-tab-group class="activity-tabs" animationDuration="200ms">
    <!-- Latest session: paid, due or covered -->
    <mat-tab>
      <ng-template mat-tab-label>
        <span class="tab-label">
          {{ 'ACTIVITY.TAB_PAYABLES' | translate }}
          @if (state.unpaidCount() > 0) {
            <span class="tab-badge">{{ state.unpaidCount() }}</span>
          }
        </span>
      </ng-template>

      <div class="tab-content">
        @if (state.isPayablesLoading()) {
          <div class="loading-state">
            <mat-spinner diameter="36"></mat-spinner>
          </div>
        } @else if (state.payableStays().length === 0) {
          <div class="empty-state">
            <div class="empty-icon-wrap">
              <mat-icon>local_parking</mat-icon>
            </div>
            <h3>{{ 'ACTIVITY.EMPTY_PAYABLES' | translate }}</h3>
          </div>
        } @else {
          <div class="payable-list">
            @for (stay of state.payableStays(); track stay.parkingProcessId) {
              <div class="payable-card" [class.due]="stay.paymentStatus === PaymentStatus.Due">
                <div class="card-header">
                  @if (stay.licensePlate) {
                    @let plate = getPlatePresentation(stay.licensePlate);
                    <app-plate
                      [number]="plate.fullNumber"
                      [euBand]="plate.euBand"
                      [countryCode]="plate.countryCode"
                      [region]="plate.region"
                      [regionSeparator]="plate.regionSeparator"
                    />
                  }
                  <span class="status-chip" [class.active]="stay.isActive" [class.ended]="!stay.isActive">
                    {{ (stay.isActive ? 'ACTIVITY.ACTIVE' : 'ACTIVITY.COMPLETED') | translate }}
                  </span>
                </div>

                <div class="card-body">
                  <div class="info-grid">
                    <div class="info-item">
                      <span class="label">{{ 'ACTIVITY.STARTED_AT' | translate }}</span>
                      <span class="value">{{ stay.startedAt | date:'medium' }}</span>
                    </div>
                    @if (stay.endedAt) {
                      <div class="info-item">
                        <span class="label">{{ 'ACTIVITY.ENDED_AT' | translate }}</span>
                        <span class="value">{{ stay.endedAt | date:'medium' }}</span>
                      </div>
                    }
                    <div class="info-item">
                      <span class="label">{{ 'ACTIVITY.DURATION' | translate }}</span>
                      <span class="value duration-value">{{ getDuration(stay.startedAt, stay.endedAt) }}</span>
                    </div>
                    @if (stay.parkingZoneName) {
                      <div class="info-item">
                        <span class="label">{{ 'ACTIVITY.ZONE' | translate }}</span>
                        <span class="value">{{ stay.parkingZoneName }}</span>
                      </div>
                    }
                    @if (stay.parkingSpaceName) {
                      <div class="info-item">
                        <span class="label">{{ 'ACTIVITY.SPACE' | translate }}</span>
                        <span class="value">{{ stay.parkingSpaceName }}</span>
                      </div>
                    }
                  </div>

                  <div class="payment-status" [ngClass]="statusClass(stay.paymentStatus)">
                    <mat-icon>{{ statusIcon(stay.paymentStatus) }}</mat-icon>
                    <span>{{ statusKey(stay.paymentStatus) | translate }}</span>
                  </div>

                  @if (stay.graceUntil && stay.paymentStatus === PaymentStatus.Paid) {
                    <div class="grace-row">
                      <span>{{ 'ACTIVITY.LEAVE_BY' | translate }}</span>
                      <span>{{ stay.graceUntil | date:'shortTime' }}</span>
                    </div>
                  }

                  @if (stay.paymentStatus === PaymentStatus.Due || stay.paymentStatus === PaymentStatus.Paid) {
                    <div class="price-breakdown">
                      @if (stay.grossAmount && stay.grossAmount > (stay.amountDue ?? 0)) {
                        <div class="price-row muted">
                          <span>{{ 'ACTIVITY.GROSS_AMOUNT' | translate }}</span>
                          <span>{{ stay.grossAmount | currency:(stay.currency || 'EUR'):'symbol':'1.2-2' }}</span>
                        </div>
                      }
                      @if (stay.netPaidAmount && stay.netPaidAmount > 0) {
                        <div class="price-row muted">
                          <span>{{ 'ACTIVITY.PAID_AMOUNT' | translate }}</span>
                          <span>-{{ stay.netPaidAmount | currency:(stay.currency || 'EUR'):'symbol':'1.2-2' }}</span>
                        </div>
                      }
                      <div class="price-row total">
                        <span>{{ 'ACTIVITY.DUE_AMOUNT' | translate }}</span>
                        <span class="due-amount">{{ (stay.amountDue ?? 0) | currency:(stay.currency || 'EUR'):'symbol':'1.2-2' }}</span>
                      </div>
                    </div>
                  }
                </div>

                @if (stay.paymentStatus === PaymentStatus.Due) {
                  <div class="card-footer">
                    <jjames-button
                      variant="primary"
                      matIconLeft="payment"
                      (clicked)="onPayStay(stay.parkingProcessId)">
                      {{ 'ACTIVITY.PAY_BUTTON' | translate }} ({{ (stay.amountDue ?? 0) | currency:(stay.currency || 'EUR'):'symbol':'1.2-2' }})
                    </jjames-button>
                  </div>
                }
              </div>
            }
          </div>
        }
      </div>
    </mat-tab>

    <!-- Completed Parking History Tab -->
    <mat-tab [label]="'ACTIVITY.TAB_HISTORY' | translate">
      <div class="tab-content">
        @if (isHistoryLoading()) {
          <div class="loading-state">
            <mat-spinner diameter="36"></mat-spinner>
          </div>
        } @else if (historyProcesses().length === 0) {
          <div class="empty-state">
            <div class="empty-icon-wrap">
              <mat-icon>history</mat-icon>
            </div>
            <h3>{{ 'ACTIVITY.EMPTY_HISTORY' | translate }}</h3>
          </div>
        } @else {
          <div class="history-list">
            @for (proc of historyProcesses(); track proc.id) {
              <div class="history-item">
                <div class="history-main">
                  <div class="history-plate">
                    @let plate = getPlatePresentation(proc.licensePlateDetails?.fullNumber);
                    <app-plate
                      [number]="plate.fullNumber"
                      [euBand]="plate.euBand"
                      [countryCode]="plate.countryCode"
                      [region]="plate.region"
                      [regionSeparator]="plate.regionSeparator"
                    />
                  </div>

                  <div class="history-details">
                    <div class="location">
                      <span class="zone">{{ proc.parkingZoneDetails?.name || 'Zone' }}</span>
                      @if (proc.parkingZoneDetails?.description) {
                        <span class="space-name">· {{ proc.parkingZoneDetails?.description }}</span>
                      }
                    </div>

                    <div class="times">
                      <span>{{ proc.startedAt | date:'mediumDate' }} {{ proc.startedAt | date:'shortTime' }}</span>
                      @if (proc.endedAt) {
                        <span>→ {{ proc.endedAt | date:'shortTime' }}</span>
                      }
                      <span class="duration-badge">({{ proc.duration || getDuration(proc.startedAt, proc.endedAt) }})</span>
                    </div>
                  </div>
                </div>

                <div class="history-status">
                  @if (proc.violationDetails) {
                    <span class="badge violation">Violation</span>
                  } @else if (!proc.endedAt) {
                    <span class="badge active">{{ 'ACTIVITY.ACTIVE' | translate }}</span>
                  } @else {
                    <span class="badge completed">{{ 'ACTIVITY.COMPLETED' | translate }}</span>
                  }
                </div>
              </div>
            }
          </div>
        }
      </div>
    </mat-tab>
  </mat-tab-group>
</div>
